import React, { useEffect, useState, useRef } from "react";
import TopNav from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowUpRight, Wallet, TrendingUp, Users, Layers, FileDown, ShieldCheck, ShieldAlert, ShieldQuestion, UserPlus, Upload, FileText, Download, Plus, History } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import Tree from "react-d3-tree";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <Card data-testid={testId} className="p-6 bg-card/60 border-border/70 rounded-2xl">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
    </div>
    <div className={`mt-4 font-display font-bold text-3xl ${accent ? "text-primary" : ""}`}>{value}</div>
  </Card>
);

export default function UserDashboard() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [kycForm, setKycForm] = useState({ aadhaar: "", pan: "", bank_holder_name: "", bank_account_number: "", bank_ifsc: "" });
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", dob: "", mobile: "", aadhaar: "", pan: "", father_name: "", address: "", pin_code: "", bank_account_number: "", bank_ifsc: "" });
  const [passbookFile, setPassbookFile] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [ledger, setLedger] = useState([]);
  const treeContainerRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 200, y: 60 });

  const load = async () => {
    try {
      const [d, t, k, c, l] = await Promise.all([
        api.get("/user/dashboard"),
        api.get("/user/tree"),
        api.get("/user/kyc"),
        api.get("/user/customers"),
        api.get("/user/wallet-ledger"),
      ]);
      setData(d.data);
      setTree(t.data);
      setKyc(k.data);
      setCustomers(c.data);
      setLedger(l.data);
      if (k.data.kyc) {
        setKycForm({
          aadhaar: k.data.kyc.aadhaar || "",
          pan: k.data.kyc.pan || "",
          bank_holder_name: k.data.kyc.bank_holder_name || "",
          bank_account_number: k.data.kyc.bank_account_number || "",
          bank_ifsc: k.data.kyc.bank_ifsc || "",
        });
      }
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (treeContainerRef.current) {
      const { width } = treeContainerRef.current.getBoundingClientRect();
      setTranslate({ x: width / 2, y: 60 });
    }
  }, [tree]);

  const downloadCertificate = async () => {
    try {
      const res = await fetch(`${API_BASE}/user/certificate`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Download failed" }));
        toast.error(err.detail || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asiyanna-Certificate-${user.referral_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Could not download certificate");
    }
  };

  const payJoiningFee = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/payments/joining-fee", { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  const openRazorpay = async (type, amount) => {
    if (!window.Razorpay) {
      toast.error("Razorpay checkout not loaded yet, retry in a moment");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/razorpay/create-order", { type, amount: amount || null });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "asiyanna",
        description: type === "joining_fee" ? "Joining Fee" : "Wallet Top-up",
        order_id: order.order_id,
        prefill: { name: order.user.name, email: order.user.email },
        theme: { color: "#00FF66" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(v.type === "wallet_topup" ? `Wallet topped up · Balance ₹${v.wallet_balance.toFixed(2)}` : "Joining fee paid — welcome!");
            setTopupOpen(false);
            await Promise.all([load(), refresh()]);
          } catch (e) {
            toast.error(apiErr(e));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rz = new window.Razorpay(options);
      rz.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed"); setBusy(false); });
      rz.open();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const startTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt < 100) { toast.error("Minimum top-up is ₹100"); return; }
    if (amt > 100000) { toast.error("Maximum single top-up is ₹1,00,000"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/payments/wallet-topup", { origin_url: window.location.origin, amount: amt });
      window.location.href = r.url;
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  const requestWithdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/user/withdraw", { amount: amt });
      toast.success("Withdrawal requested");
      setAmount("");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const submitKyc = async () => {
    setBusy(true);
    try {
      await api.post("/user/kyc", {
        aadhaar: kycForm.aadhaar.replace(/\s/g, ""),
        pan: kycForm.pan.trim().toUpperCase(),
        bank_holder_name: kycForm.bank_holder_name.trim(),
        bank_account_number: kycForm.bank_account_number.trim(),
        bank_ifsc: kycForm.bank_ifsc.trim().toUpperCase(),
      });
      toast.success("KYC submitted — pending admin review");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const addCustomer = async () => {
    if (!newCust.name || !newCust.dob || !newCust.mobile || !newCust.aadhaar || !newCust.pan) {
      toast.error("Please fill all required fields (name, DOB, mobile, Aadhaar, PAN)");
      return;
    }
    if (!newCust.father_name || !newCust.address || !newCust.pin_code || !newCust.bank_account_number || !newCust.bank_ifsc) {
      toast.error("Please fill father's name, address, pin code, bank account no. and IFSC");
      return;
    }
    if (!passbookFile) {
      toast.error("Upload the bank passbook (image or PDF)");
      return;
    }
    const form = new FormData();
    form.append("name", newCust.name.trim());
    form.append("dob", newCust.dob);
    form.append("mobile", newCust.mobile.trim());
    form.append("aadhaar", newCust.aadhaar.replace(/\s/g, ""));
    form.append("pan", newCust.pan.trim().toUpperCase());
    form.append("father_name", newCust.father_name.trim());
    form.append("address", newCust.address.trim());
    form.append("pin_code", newCust.pin_code.trim());
    form.append("bank_account_number", newCust.bank_account_number.trim());
    form.append("bank_ifsc", newCust.bank_ifsc.trim().toUpperCase());
    form.append("passbook", passbookFile);
    setBusy(true);
    try {
      const { data } = await api.post("/user/customers", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.pair_bonus && data.pair_bonus > 0) {
        toast.success(`Customer added · ₹1000 fee · Pair bonus +₹${data.pair_bonus} · Balance ₹${data.wallet_balance.toFixed(2)}`);
      } else {
        toast.success(`Customer added · ₹1000 deducted · Balance ₹${data.wallet_balance.toFixed(2)}`);
      }
      setCustOpen(false);
      setNewCust({ name: "", dob: "", mobile: "", aadhaar: "", pan: "", father_name: "", address: "", pin_code: "", bank_account_number: "", bank_ifsc: "" });
      setPassbookFile(null);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadCustomerCertificate = async (customerId, customerName) => {
    try {
      const res = await fetch(`${API_BASE}/user/customers/${customerId}/certificate`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Download failed" }));
        toast.error(err.detail || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (customerName || "customer").replace(/[^A-Za-z0-9_-]/g, "");
      a.download = `asiyanna-Certificate-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Could not download certificate");
    }
  };

  const downloadPassbook = async (customerId, filename) => {
    try {
      const res = await fetch(`${API_BASE}/user/customers/${customerId}/passbook`, { credentials: "include" });
      if (!res.ok) { toast.error("Download failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "passbook";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };

  if (!data || !user) return <div className="min-h-screen bg-background"><TopNav /><div className="p-8 text-muted-foreground">Loading dashboard...</div></div>;

  return (
    <div className="min-h-screen">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10 fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="tag mb-3"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Member Dashboard</div>
            <h1 data-testid="user-welcome" className="font-display font-black text-3xl sm:text-4xl">Hey, {user.name.split(" ")[0]}.</h1>
            <p className="text-muted-foreground mt-1 text-sm">Your referral code: <span data-testid="user-ref-code" className="font-mono text-primary">{user.referral_code}</span></p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {user.joining_fee_paid && (
              <Button data-testid="add-money-btn" onClick={() => setTopupOpen(true)} className="rounded-full h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 glow-pill">
                <Plus className="mr-2 w-4 h-4" /> Add Money
              </Button>
            )}
            {user.joining_fee_paid && (
              <Button data-testid="add-customer-btn" onClick={() => setCustOpen(true)} variant="outline" className="rounded-full h-11 px-5 border-accent/50 text-accent hover:bg-accent/10 hover:text-accent">
                <UserPlus className="mr-2 w-4 h-4" /> Add Customer
              </Button>
            )}
            {user.joining_fee_paid && (
              <Button data-testid="download-certificate-btn" onClick={downloadCertificate} variant="outline" className="rounded-full h-11 px-5 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
                <FileDown className="mr-2 w-4 h-4" /> Shareholder Certificate
              </Button>
            )}
            {!user.joining_fee_paid && (
              <div className="flex gap-2">
                <Button data-testid="pay-joining-fee-btn" onClick={payJoiningFee} disabled={busy} className="rounded-full h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 glow-pill">
                  Stripe · ₹999 <ArrowUpRight className="ml-2 w-4 h-4" />
                </Button>
                <Button data-testid="pay-joining-fee-razorpay-btn" onClick={() => openRazorpay("joining_fee")} disabled={busy} variant="outline" className="rounded-full h-11 px-5 border-accent/50 text-accent hover:bg-accent/10 hover:text-accent">
                  Razorpay · ₹999 <ArrowUpRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {!user.joining_fee_paid && (
          <div data-testid="joining-fee-banner" className="mb-8 p-5 rounded-2xl border border-primary/30 bg-primary/5">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Action Required</div>
            <div className="text-sm text-foreground">Pay the one-time ₹999 joining fee to activate your account & unlock commissions.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat testId="stat-wallet" icon={Wallet} label="Wallet Balance" value={`₹${data.stats.wallet_balance.toFixed(2)}`} accent />
          <Stat testId="stat-earnings" icon={TrendingUp} label="Total Earnings" value={`₹${data.stats.total_earnings.toFixed(2)}`} />
          <Stat testId="stat-direct" icon={Users} label="Direct Referrals" value={data.stats.direct_referrals} />
          <Stat testId="stat-downline" icon={Layers} label="Total Downline" value={data.stats.downline_total} />
        </div>

        <Card className="p-6 bg-card/60 border-border/70 rounded-2xl mb-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Your Referral Code</div>
              <div className="font-display font-bold text-2xl text-primary font-mono">{user.referral_code}</div>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Share code with new members</div>
          </div>
        </Card>

        <Tabs defaultValue="tree" className="w-full">
          <TabsList data-testid="dashboard-tabs" className="bg-card/60 border border-border/70 rounded-full h-11 p-1">
            <TabsTrigger data-testid="tab-tree" value="tree" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Network</TabsTrigger>
            <TabsTrigger data-testid="tab-commissions" value="commissions" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Commissions</TabsTrigger>
            <TabsTrigger data-testid="tab-withdraw" value="withdraw" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Withdraw</TabsTrigger>
            <TabsTrigger data-testid="tab-kyc" value="kyc" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">KYC</TabsTrigger>
            <TabsTrigger data-testid="tab-customers" value="customers" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Customers</TabsTrigger>
            <TabsTrigger data-testid="tab-ledger" value="ledger" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Ledger</TabsTrigger>
          </TabsList>

          <TabsContent value="tree" className="mt-6">
            <Card data-testid="tree-card" className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div ref={treeContainerRef} className="w-full" style={{ height: 480 }}>
                {tree && (
                  <Tree
                    data={tree}
                    orientation="vertical"
                    translate={translate}
                    pathFunc="step"
                    zoomable
                    collapsible={false}
                    separation={{ siblings: 1.2, nonSiblings: 1.6 }}
                    nodeSize={{ x: 180, y: 90 }}
                  />
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="commissions" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl">
              <div className="p-4 border-b border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground">Commission history</div>
              <div className="divide-y divide-border/40">
                {data.commissions.length === 0 && <div data-testid="no-commissions" className="p-8 text-sm text-muted-foreground">No commissions yet. Invite people using your referral link.</div>}
                {data.commissions.map((c) => (
                  <div key={c.id} data-testid={`commission-${c.id}`} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                    <div>
                      <div className="text-sm font-medium">Level {c.level} · {c.percentage}%</div>
                      <div className="text-xs text-muted-foreground font-mono">from {c.from_user_email}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-primary">+₹{c.amount.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="withdraw" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6 bg-card/60 border-border/70 rounded-2xl">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Request payout</div>
                <div className="font-display font-bold text-lg mb-4">Available: <span className="text-primary">₹{data.stats.wallet_balance.toFixed(2)}</span></div>
                <div className="space-y-3">
                  <Input data-testid="withdraw-amount-input" type="number" min="100" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (min ₹100)" className="h-12 bg-background border-border focus:border-primary focus:ring-primary" />
                  <Button data-testid="withdraw-submit-btn" onClick={requestWithdraw} disabled={busy || !user.joining_fee_paid} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy ? "Submitting..." : "Request Withdrawal"}
                  </Button>
                  {!user.joining_fee_paid && <div className="text-xs text-muted-foreground">Pay joining fee first to unlock withdrawals.</div>}
                </div>
              </Card>
              <Card className="bg-card/60 border-border/70 rounded-2xl">
                <div className="p-4 border-b border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground">History</div>
                <div className="divide-y divide-border/40 max-h-80 overflow-auto">
                  {data.withdrawals.length === 0 && <div data-testid="no-withdrawals" className="p-6 text-sm text-muted-foreground">No withdrawals yet.</div>}
                  {data.withdrawals.map((w) => (
                    <div key={w.id} data-testid={`withdrawal-${w.id}`} className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-mono font-bold">₹{w.amount.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString()}</div>
                      </div>
                      <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
                        w.status === "approved" ? "border-primary/50 text-primary bg-primary/10"
                        : w.status === "rejected" ? "border-destructive/50 text-destructive bg-destructive/10"
                        : "border-accent/50 text-accent bg-accent/10"
                      }`}>{w.status}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="kyc" className="mt-6">
            {(() => {
              const status = kyc?.kyc_status || "not_submitted";
              const StatusIcon = status === "approved" ? ShieldCheck : status === "rejected" ? ShieldAlert : ShieldQuestion;
              const statusColor = status === "approved" ? "text-primary border-primary/50 bg-primary/10"
                : status === "rejected" ? "text-destructive border-destructive/50 bg-destructive/10"
                : status === "pending" ? "text-accent border-accent/50 bg-accent/10"
                : "text-muted-foreground border-border bg-card/40";
              const locked = status === "approved" || status === "pending";
              return (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card data-testid="kyc-status-card" className="p-6 bg-card/60 border-border/70 rounded-2xl">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs uppercase tracking-[0.15em] ${statusColor}`}>
                      <StatusIcon className="w-3.5 h-3.5" /> {status.replace("_", " ")}
                    </div>
                    <div className="mt-4 font-display font-bold text-lg">KYC Verification</div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Submit your Aadhaar, PAN and bank details to unlock withdrawals. Payouts are gated on KYC approval.
                    </p>
                    {kyc?.kyc?.note && (
                      <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-xs">
                        <div className="uppercase tracking-[0.15em] text-destructive mb-1">Admin note</div>
                        <div className="text-foreground">{kyc.kyc.note}</div>
                      </div>
                    )}
                  </Card>
                  <Card className="p-6 bg-card/60 border-border/70 rounded-2xl lg:col-span-2">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Identity & Bank details</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Aadhaar (12 digits)</Label>
                        <Input data-testid="kyc-aadhaar-input" disabled={locked} maxLength={12} value={kycForm.aadhaar} onChange={(e) => setKycForm({ ...kycForm, aadhaar: e.target.value.replace(/\D/g, "") })} placeholder="123412341234" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">PAN</Label>
                        <Input data-testid="kyc-pan-input" disabled={locked} maxLength={10} value={kycForm.pan} onChange={(e) => setKycForm({ ...kycForm, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Account holder name</Label>
                        <Input data-testid="kyc-holder-input" disabled={locked} value={kycForm.bank_holder_name} onChange={(e) => setKycForm({ ...kycForm, bank_holder_name: e.target.value })} placeholder="Full name as on bank" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bank account number</Label>
                        <Input data-testid="kyc-account-input" disabled={locked} value={kycForm.bank_account_number} onChange={(e) => setKycForm({ ...kycForm, bank_account_number: e.target.value.replace(/\D/g, "") })} placeholder="123456789012" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">IFSC code</Label>
                        <Input data-testid="kyc-ifsc-input" disabled={locked} maxLength={11} value={kycForm.bank_ifsc} onChange={(e) => setKycForm({ ...kycForm, bank_ifsc: e.target.value.toUpperCase() })} placeholder="SBIN0001234" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                      </div>
                    </div>
                    <Button data-testid="kyc-submit-btn" onClick={submitKyc} disabled={busy || locked} className="mt-6 rounded-full h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 glow-pill">
                      {status === "approved" ? "Verified" : status === "pending" ? "Awaiting review" : status === "rejected" ? "Resubmit KYC" : "Submit for review"}
                    </Button>
                  </Card>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your customers ({customers.length})</div>
                <Button data-testid="customers-add-btn" onClick={() => setCustOpen(true)} size="sm" className="rounded-full h-9 px-4 bg-primary text-primary-foreground hover:bg-primary/90">
                  <UserPlus className="w-4 h-4 mr-1" /> Add Customer
                </Button>
              </div>              <div className="divide-y divide-border/40 max-h-[540px] overflow-auto">
                {customers.length === 0 && <div data-testid="no-customers" className="p-8 text-sm text-muted-foreground">No customers added yet.</div>}
                {customers.map((c) => (
                  <div key={c.id} data-testid={`customer-${c.id}`} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center hover:bg-white/[0.02]">
                    <div className="md:col-span-2">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">DOB {c.dob}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile</div>
                      <div className="font-mono text-sm">{c.mobile}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Aadhaar</div>
                      <div className="font-mono text-sm">{c.aadhaar}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">PAN</div>
                      <div className="font-mono text-sm">{c.pan}</div>
                    </div>
                    <div className="flex justify-end">
                      <Button data-testid={`customer-certificate-${c.id}`} size="sm" variant="outline" onClick={() => downloadCustomerCertificate(c.id, c.name)} className="rounded-full border-primary/40 text-primary hover:bg-primary/10 hover:text-primary h-9 px-3">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> Certificate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="ledger" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <History className="w-4 h-4" /> Wallet ledger ({ledger.length})
              </div>
              <div className="divide-y divide-border/40 max-h-[560px] overflow-auto">
                {ledger.length === 0 && <div data-testid="no-ledger" className="p-8 text-sm text-muted-foreground">No wallet transactions yet.</div>}
                {ledger.map((l) => (
                  <div key={l.id} data-testid={`ledger-${l.id}`} className="p-4 flex items-center justify-between gap-4 flex-wrap hover:bg-white/[0.02]">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{l.note || (l.action === "credit" ? "Wallet credit" : "Wallet debit")}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(l.created_at).toLocaleString()} · By <span className="font-mono">{
                          l.admin_email === "system" ? "system"
                          : l.admin_email === "stripe" ? "stripe"
                          : "admin"
                        }</span>
                      </div>
                    </div>
                    <div className={`font-mono font-bold text-lg ${l.action === "credit" ? "text-primary" : "text-destructive"}`}>
                      {l.action === "credit" ? "+" : "−"}₹{l.amount.toFixed(2)}
                    </div>
                    <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
                      l.action === "credit" ? "border-primary/50 text-primary bg-primary/10" : "border-destructive/50 text-destructive bg-destructive/10"
                    }`}>{l.action}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

        </Tabs>

        <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
          <DialogContent data-testid="topup-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Add money to wallet</DialogTitle>
              <DialogDescription>Top up via Stripe. Money is credited instantly after payment.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-4 gap-2">
                {[500, 1000, 2000, 5000].map((v) => (
                  <Button
                    key={v}
                    data-testid={`topup-preset-${v}`}
                    type="button"
                    onClick={() => setTopupAmount(String(v))}
                    variant={String(v) === topupAmount ? "default" : "outline"}
                    className={`rounded-full h-10 ${String(v) === topupAmount ? "bg-primary text-primary-foreground" : "border-border"}`}
                  >
                    ₹{v.toLocaleString()}
                  </Button>
                ))}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Custom amount (min ₹100)</Label>
                <Input
                  data-testid="topup-amount-input"
                  type="number"
                  min="100"
                  max="100000"
                  step="1"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  placeholder="Enter amount in ₹"
                  className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono"
                />
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                You'll be redirected to Stripe's secure checkout. Your wallet is credited the moment payment succeeds.
              </div>
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button data-testid="topup-cancel-btn" variant="ghost" onClick={() => setTopupOpen(false)} className="rounded-full">Cancel</Button>
              <Button data-testid="topup-razorpay-btn" onClick={() => openRazorpay("wallet_topup", parseFloat(topupAmount))} disabled={busy || !topupAmount} variant="outline" className="rounded-full border-accent/50 text-accent hover:bg-accent/10 hover:text-accent">
                {busy ? "..." : `Razorpay ${topupAmount ? `₹${topupAmount}` : ""}`}
              </Button>
              <Button data-testid="topup-pay-btn" onClick={startTopup} disabled={busy || !topupAmount} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 glow-pill">
                {busy ? "Redirecting..." : `Stripe ${topupAmount ? `₹${topupAmount}` : ""}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={custOpen} onOpenChange={setCustOpen}>
          <DialogContent data-testid="add-customer-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Add customer</DialogTitle>
              <DialogDescription>
                Record a new customer with their identity documents and bank passbook.
                <br />
                <span className="text-destructive font-medium">₹1,000 will be deducted from your wallet.</span>
                <br />
                <span className="text-primary font-medium">Every 2 customers (1 pair) = ₹300 bonus credited automatically.</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Full name</Label>
                  <Input data-testid="cust-name-input" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} placeholder="Ramesh Kumar" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Date of birth</Label>
                  <Input data-testid="cust-dob-input" type="date" value={newCust.dob} onChange={(e) => setNewCust({ ...newCust, dob: e.target.value })} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mobile no.</Label>
                  <Input data-testid="cust-mobile-input" value={newCust.mobile} onChange={(e) => setNewCust({ ...newCust, mobile: e.target.value.replace(/\D/g, "") })} maxLength={15} placeholder="9876543210" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Aadhaar no. (12 digits)</Label>
                  <Input data-testid="cust-aadhaar-input" value={newCust.aadhaar} onChange={(e) => setNewCust({ ...newCust, aadhaar: e.target.value.replace(/\D/g, "") })} maxLength={12} placeholder="123412341234" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">PAN no.</Label>
                  <Input data-testid="cust-pan-input" value={newCust.pan} onChange={(e) => setNewCust({ ...newCust, pan: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Father's name</Label>
                  <Input data-testid="cust-father-input" value={newCust.father_name} onChange={(e) => setNewCust({ ...newCust, father_name: e.target.value })} maxLength={100} placeholder="Suresh Kumar" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Address</Label>
                  <Input data-testid="cust-address-input" value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} maxLength={500} placeholder="House / Street / City / State" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pin code</Label>
                  <Input data-testid="cust-pin-input" value={newCust.pin_code} onChange={(e) => setNewCust({ ...newCust, pin_code: e.target.value.replace(/\D/g, "") })} maxLength={10} placeholder="700001" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bank account no.</Label>
                  <Input data-testid="cust-bank-acc-input" value={newCust.bank_account_number} onChange={(e) => setNewCust({ ...newCust, bank_account_number: e.target.value.replace(/\D/g, "") })} maxLength={25} placeholder="123456789012" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">IFSC code</Label>
                  <Input data-testid="cust-ifsc-input" value={newCust.bank_ifsc} onChange={(e) => setNewCust({ ...newCust, bank_ifsc: e.target.value.toUpperCase() })} maxLength={11} placeholder="SBIN0001234" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bank passbook (image or PDF, max 8 MB)</Label>
                <label htmlFor="passbook-upload" className="mt-2 flex items-center gap-3 h-14 px-4 rounded-lg border border-dashed border-border hover:border-primary/50 bg-background cursor-pointer transition-colors">
                  <Upload className="w-5 h-5 text-primary" />
                  <span className="text-sm truncate flex-1">
                    {passbookFile ? passbookFile.name : "Click to select file"}
                  </span>
                  {passbookFile && <span className="text-xs text-muted-foreground">{(passbookFile.size / 1024).toFixed(0)} KB</span>}
                </label>
                <input
                  id="passbook-upload"
                  data-testid="cust-passbook-input"
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setPassbookFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button data-testid="cust-cancel-btn" variant="ghost" onClick={() => setCustOpen(false)} className="rounded-full">Cancel</Button>
              <Button data-testid="cust-create-btn" onClick={addCustomer} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? "Uploading..." : "Save customer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
